'use client';
import { IconButton, type IconButtonProps } from '@repo/ui';
import Link from 'next/link';
import type { HTMLAttributeAnchorTarget } from 'react';

import { DefaultContainer } from '@/app/_components/layout-content/LayoutItems';
import { useIsDeeplinkFallback } from '@/hooks/useIsDeeplinkFallback';
import { SERVICE_LINKS } from '@/lib/constants/common';

const SNS_ICON_TYPES = ['instagram', 'naver-blog', 'youtube'] as const;
type SNSIconType = (typeof SNS_ICON_TYPES)[number];

const ListTitle = ({ text }: { text: string }) => <li className={'mb-tiny-large font-medium text-neutral-foreground-main'}>{text}</li>;

const TextButton = ({ link, text, target = '_blank' }: { link: string; text: string; target?: HTMLAttributeAnchorTarget }) => (
  <li className={'py-tiny-medium'}>
    <Link href={link} target={target}>
      {text}
    </Link>
  </li>
);

const SNSButton = ({ link, iconType }: { link: string; iconType: SNSIconType }) => (
  <Link href={link} target="_blank">
    <IconButton iconType={iconType as IconButtonProps['iconType']} size={'small'} />
  </Link>
);

export const DefaultFooter = () => {
  const isDeeplinkFallback = useIsDeeplinkFallback();

  return (
    <footer className="flex justify-center py-spacious-medium bg-neutral-background-lowest">
      <DefaultContainer className={'text-neutral-foreground-low body-medium-regular !container-xlarge !container-padding-medium'}>
        <div>
          {!isDeeplinkFallback && (
            <div className={'flex justify-between mb-spacious-small compact:flex-col'}>
              <div>
                <div className={'flex flex-wrap gap-spacious-medium mb-spacious-small'}>
                  <ul className={'list-none p-0 my-0'}>
                    <ListTitle text={'서비스'} />
                    <TextButton link={SERVICE_LINKS['bznav-refund']} text="비즈넵 환급" />
                    <TextButton link={SERVICE_LINKS['bznav-care']} text="비즈넵 케어" />
                    <TextButton link={SERVICE_LINKS['bznav-sena']} text="비즈넵 세나" />
                    <TextButton
                      link="https://ai.bznav.com/calc?utm_source=bznav.homepage&utm_campaign=bznav_homepage_to_sena&utm_medium=organic&utm_content=nav_menu_click"
                      text="비즈넵 계산기"
                    />
                    <TextButton link="/brand-resource" text="브랜드 리소스" />
                  </ul>
                  <ul className={'list-none p-0 my-0'}>
                    <ListTitle text={'회사'} />
                    <TextButton link="https://www.zent.kr/" text="회사 소개" />
                    <TextButton link="https://zent.oopy.io/" text="채용" />
                  </ul>
                  <ul className={'list-none p-0 my-0'}>
                    <ListTitle text={'고객센터'} />
                    <TextButton link="https://help.bznav.com/hc/ko" text="비즈넵 환급 고객센터" />
                    <TextButton link="https://care.bznav.com/cs-center" text="비즈넵 케어 고객센터" />
                  </ul>
                </div>
                <div className={'flex gap-normal-xlarge body-small-regular compact:mb-spacious-small'}>
                  <span>
                    <Link href="/terms/service" target="_self">
                      서비스 이용약관
                    </Link>
                  </span>
                  <span className={'font-medium text-neutral-foreground-main'}>
                    <Link href="/terms/privacy" target="_self">
                      개인정보 처리방침
                    </Link>
                  </span>
                </div>
              </div>
              <div className={'flex gap-tiny-large h-fit'}>
                <SNSButton link="https://www.instagram.com/bznav.today/" iconType={'instagram'} />
                <SNSButton link="https://blog.naver.com/bznav_official_" iconType={'naver-blog'} />
                <SNSButton link="https://www.youtube.com/channel/UC3fbzIYZENFZ0IrBW4pRpDA" iconType={'youtube'} />
              </div>
            </div>
          )}
          <div>
            <div className={'body-small-medium text-neutral-foreground-main'}>지엔터프라이즈(주)</div>
            <div className={'flex flex-col pt-normal-medium body-small-regular mb-spacious-small tracking-normal'}>
              <ul className={'list-none px-0 py-0.5 my-0 body-14 flex compact:flex-col items-center compact:items-start'}>
                <li className="pb-tiny-medium">대표 이성봉</li>
                <li className={'divider compact:hidden bg-neutral-background-low'} />
                <li className="pb-tiny-medium">사업자번호 236-88-00397</li>
                <li className={'divider compact:hidden bg-neutral-background-low'} />
                <li className="pb-tiny-medium">통신판매업신고 제2016-서울강남-04379호</li>
              </ul>
              <ul className={'list-none px-0 py-0.5 compact:py-0 my-0 body-14 flex compact:flex-col items-center compact:items-start'}>
                <li className="pb-tiny-medium">서울특별시 강남구 테헤란로 507, 9층</li>
                <li className={'divider compact:hidden bg-neutral-background-low'} />
                <li className="pb-tiny-medium">제휴 문의 contact@zent.kr</li>
                <li className={'divider compact:hidden bg-neutral-background-low'} />
                <li className="pb-tiny-medium">사업자 문의 help@zent.kr</li>
              </ul>
            </div>
            <div className={'caption-medium-regular'}>Copyright © Z Enterprise, Inc. All rights reserved.</div>
          </div>
        </div>
      </DefaultContainer>
    </footer>
  );
};
